import {UserId} from 'records/User';
import {
  AxisState,
  DataPoint,
  Metric,
  MetricFilter,
  MetricInfo,
  MetricInfoGroup,
  MetricParams,
  MetricRange,
} from '@store/@types/metric';
import {KpiTarget} from '@store/@types/kpi';
import {IdMap} from '@store/@types/util';
import {List, Map} from 'immutable';
import Skill from 'records/Skill';
import {HORIZONTAL_METRICS, VERTICAL_METRICS} from '@store/metrics-trends/metrics-trends.consts';
import Team from 'records/Team';
import {Moment} from 'moment';
import {formatMetric, getAxisValue} from '@store/metrics-trends/metrics-trends.utils';
import {getRange} from 'components/dashboard/manager/nine-box/GraphRange.utils';
import {TeamId} from 'records/TeamId';

const kpiOptions = (kpisById: IdMap<KpiTarget>) =>
  kpisById
    .toList()
    .filter(kpi => kpi.chartAsSkill)
    .map(kpi => ({
      title: kpi.name,
      value: new MetricParams({
        metric: Metric.KPI_OBSERVATION,
        context: kpi.id + '',
      }),
      group: MetricInfoGroup.KPI,
    }));

const skillOptions = (usedSkills: List<Skill>) =>
  usedSkills.map(skill => ({
    title: `Skill: ${skill.skill}`,
    value: new MetricParams({
      metric: Metric.CALL_SCORING_SKILLS,
      context: skill.id + '',
      direction: 'any',
    }),
    group: MetricInfoGroup.SCORED_SKILLS,
  }));

export function xAxisOptions(usedSkills: List<Skill>, kpisById: IdMap<KpiTarget>): MetricInfo[] {
  return [...kpiOptions(kpisById), ...skillOptions(usedSkills), ...HORIZONTAL_METRICS];
}

export function yAxisOptions(kpisById: IdMap<KpiTarget>) {
  return [...kpiOptions(kpisById), ...VERTICAL_METRICS];
}

export function titleByMetric(usedSkills: List<Skill>, kpisById: IdMap<KpiTarget>) {
  const allPairs = VERTICAL_METRICS.concat(HORIZONTAL_METRICS)
    .concat(skillOptions(usedSkills))
    .concat(kpiOptions(kpisById))
    .map(m => [m.value, m.title] as [MetricParams, string]);

  return Map(allPairs) as Map<MetricParams, string>;
}

// Choose teams to render based on if we have selected a team, or if we're viewing as a manager
export function getTeams(
  managerId: UserId,
  selectedTeamId: TeamId,
  allTeams: List<Team>,
  managerTeams: List<Team>
): TeamId[] {
  if (selectedTeamId) return [selectedTeamId];
  if (managerId) return managerTeams.map(t => t.id).toJS();
  return allTeams.map(t => t.id).toJS();
}

const DATE_FORMAT = 'YYYY-MM-DD' as const;
export function getMetricFilter(
  startDate: Moment,
  endDate: Moment,
  teams: TeamId[]
): MetricFilter | undefined {
  if (!teams.length) return;
  return {
    startDate: startDate.format(DATE_FORMAT),
    endDate: endDate.format(DATE_FORMAT),
    teams,
  };
}

export interface GraphGoal {
  kpi: KpiTarget;
  // formatted according to kpi
  valueStr: string;
  // value adjusted for graph range
  coordinate: number;
}

function goalInfo(
  range: MetricRange,
  metricParams: MetricParams,
  kpisById: IdMap<KpiTarget>
): GraphGoal | undefined {
  if (metricParams.metric !== Metric.KPI_OBSERVATION) return;

  const kpi = kpisById.get(parseInt(metricParams.context!, 10));
  if (!kpi) return;

  return {
    kpi,
    coordinate: getAxisValue(range, kpi.value),
    valueStr: formatMetric(kpi.value, metricParams, kpisById),
  };
}

export function getGoals(
  horizontal: AxisState,
  vertical: AxisState,
  kpisById: IdMap<KpiTarget>
): {xGoal?: GraphGoal; yGoal?: GraphGoal} {
  const xHasGoal = horizontal.metric.metric === Metric.KPI_OBSERVATION;
  const yHasGoal = vertical.metric.metric === Metric.KPI_OBSERVATION;
  if (!xHasGoal && !yHasGoal) return {};

  const range = getRange(horizontal, vertical, kpisById);
  if (!range) return {};

  return {
    xGoal: goalInfo(range.xRange, horizontal.metric, kpisById),
    yGoal: goalInfo(range.yRange, vertical.metric, kpisById),
  };
}

export function dropNulls(axis: AxisState, repId?: UserId): DataPoint[] {
  const data = repId
    ? axis.data?.[repId]?.data ?? []
    : Object.values(axis.data).flatMap(x => x?.data ?? []);
  return data.filter(d => d.value !== null) as DataPoint[];
}

export function axisLabel(
  titleMap: Map<MetricParams, string>,
  metricParams: MetricParams,
  kpisById: IdMap<KpiTarget>,
  value: number
) {
  const metricName = titleMap.get(metricParams) ?? '';
  const formattedValue = formatMetric(value, metricParams, kpisById);
  return `${metricName}: ${formattedValue}`;
}
